import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';

import { Link } from '@tanstack/react-router';
import classNames from 'classnames';
import type React from 'react';
import { useState } from 'react';
import styles from './index.module.scss';

import IconBackArrow from './assets/icon_back_allow.svg';
import IconClose from './assets/icon_close.svg';
import IconMenu from './assets/icon_menu.svg';

import { Accordion } from '@/routes/_embaUserLayout/-components/Accordion';
import { CertificateButton } from '@/routes/_embaUserLayout/-components/CertificateButton';
import { SkillCheckTestButton } from '@/routes/_embaUserLayout/-components/SkillCheckTestButton';

export const USER_ATTENDANCE_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaUserAttendanceForSidebar on UserAttendance {
    id
    slug
    status
    skillCheckTestAccessible
    certificate {
      fileRedirectUrl
    }
    embaCourse {
      name
      slug
      chapters {
        id
        ...EmbaCourseChapter
      }
    }
  }
`);

export type SideBarProps = {
  embaUserAttendance: FragmentType<typeof USER_ATTENDANCE_FRAGMENT>;
};

export const SideBar = ({ embaUserAttendance }: SideBarProps) => {
  const embaUserAttendanceData = useFragment(
    USER_ATTENDANCE_FRAGMENT,
    embaUserAttendance,
  );

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  // 内部クリックでイベントを伝播させない
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <button
        onClick={() => setIsMenuOpen(true)}
        type="button"
        className={classNames(styles.sidebarOpen, {
          [styles.show]: !isMenuOpen,
        })}
      >
        <img src={IconMenu} alt="" />
        メニュー
      </button>
      <div
        className={classNames(styles.sidebarContent, {
          [styles.show]: isMenuOpen,
        })}
        onClick={() => setIsMenuOpen(false)}
        role="button"
        tabIndex={0}
      >
        <div
          className={classNames(styles.sidebarContentInner, {
            [styles.show]: isMenuOpen,
          })}
          onClick={stopPropagation}
          role="button"
          tabIndex={0}
        >
          <div className={styles.sidebarHeader}>
            <Link
              to="/"
              className={classNames(styles.courseListLink, 'primaryShadow')}
            >
              <img
                src={IconBackArrow}
                className={styles.iconBackArrow}
                alt=""
              />
              <span>科目一覧に戻る</span>
            </Link>
            <button
              onClick={() => setIsMenuOpen(false)}
              className={styles.closeButton}
              type="button"
            >
              <img src={IconClose} alt="" className={styles.iconClose} />
              <span>メニューを閉じる</span>
            </button>
          </div>
          <div className={styles.sidebarContentInnerPadding}>
            <h3 className={styles.courseTitle}>
              {embaUserAttendanceData.embaCourse.name}
            </h3>
            <ul>
              {embaUserAttendanceData.embaCourse.chapters.map((chapter) => (
                <Accordion
                  key={chapter.id}
                  chapter={chapter}
                  onClick={() => setIsMenuOpen(false)}
                  embaUserAttendanceSlug={embaUserAttendanceData.slug}
                  type="sidebar"
                />
              ))}
            </ul>
          </div>

          <div className={styles.sidebarFooter}>
            <div className={styles.skillCheckTestArea}>
              <SkillCheckTestButton
                userAttendanceSlug={embaUserAttendanceData.slug}
                disabled={!embaUserAttendanceData.skillCheckTestAccessible}
                completed={embaUserAttendanceData.status === 'completed'}
                onClick={() => setIsMenuOpen(false)}
              />
            </div>
            <div className={styles.certificateArea}>
              <CertificateButton
                fileRedirectUrl={
                  embaUserAttendanceData.certificate?.fileRedirectUrl
                }
                disabled={!embaUserAttendanceData.certificate}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
